import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Autocomplete,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';
import HistoryIcon from '@mui/icons-material/History';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import SummarizeIcon from '@mui/icons-material/Summarize';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { Chip } from '@mui/material';
import DataTable, { Column } from '../../components/common/DataTable';
import DateRangePicker, { getDefaultDateRange, formatDateRange } from '../../components/common/DateRangePicker';
import { useWorkHours, useUpdateTrip, useSyncWorkHours, useSyncAllWorkHours, useFmsComparison, useDrivers, useCreateWorkHourEntry, useDeleteWorkHourEntry, useTrailers } from '../../hooks/useApi';
import { api } from '../../services/api';
import type { WorkHourEntry, DateRange, FmsComparisonEntry, Driver, Trailer } from '../../types';

function formatTime(dateTime: string | undefined | null): string {
  if (!dateTime) return '-';
  try {
    const d = new Date(dateTime);
    return d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '-';
  }
}

function formatOdometer(odometer: string | undefined | null): string {
  if (!odometer) return '-';
  const num = parseFloat(odometer);
  if (isNaN(num)) return odometer;
  return Math.round(num).toLocaleString('da-DK') + ' km';
}

// Format minutes to duration string (e.g. "8t 30m")
function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}t`;
  return `${hours}t ${mins}m`;
}

// Convert ISO datetime to local datetime-local input value
function toDateTimeLocal(dateTime: string | undefined | null): string {
  if (!dateTime) return '';
  try {
    const d = new Date(dateTime);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

// Format deviation between DORA and FMS times as a colored chip
function DeviationChip({ doraTime, fmsTime }: { doraTime: string | null | undefined; fmsTime: string | null }) {
  if (!doraTime || !fmsTime) return <span>-</span>;
  const doraMs = new Date(doraTime).getTime();
  const fmsMs = new Date(fmsTime).getTime();
  if (isNaN(doraMs) || isNaN(fmsMs)) return <span>-</span>;
  const diffMin = Math.round((doraMs - fmsMs) / 60000);
  const absDiff = Math.abs(diffMin);
  const label = `${diffMin > 0 ? '+' : ''}${diffMin}m`;
  const color: 'success' | 'warning' | 'error' = absDiff <= 15 ? 'success' : absDiff <= 60 ? 'warning' : 'error';
  return <Chip label={label} size="small" color={color} variant="outlined" sx={{ fontWeight: 600, minWidth: 56 }} />;
}

// Column visibility options
type ColumnKey = 'pause' | 'totalTime' | 'workTime' | 'startOdo' | 'endOdo' | 'startNote' | 'endNote';

const DEFAULT_VISIBLE_COLUMNS: Record<ColumnKey, boolean> = {
  pause: true,
  totalTime: true,
  workTime: true,
  startOdo: true,
  endOdo: true,
  startNote: true,
  endNote: true,
};

export default function WorkHoursList() {
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const { from, to } = formatDateRange(dateRange);

  const { data, isLoading, error } = useWorkHours(from, to);
  const workHours = data?.entries;
  const lastSyncedAt = data?.lastSyncedAt;
  const needsSync = data?.needsSync;
  const updateTrip = useUpdateTrip();
  const syncWorkHours = useSyncWorkHours();
  const syncAllWorkHours = useSyncAllWorkHours();
  const createWorkHour = useCreateWorkHourEntry();
  const deleteWorkHour = useDeleteWorkHourEntry();
  const { data: allTrailers } = useTrailers();

  // Auto-sync when data is stale (> 2 hours old)
  const hasAutoSynced = useRef(false);
  useEffect(() => {
    if (needsSync && !syncWorkHours.isPending && !hasAutoSynced.current) {
      hasAutoSynced.current = true;
      syncWorkHours.mutate();
    }
  }, [needsSync, syncWorkHours]);

  const [editOpen, setEditOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<WorkHourEntry | null>(null);
  const [editStartTime, setEditStartTime] = useState('');
  const [editStartOdo, setEditStartOdo] = useState('');
  const [editStartNote, setEditStartNote] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editEndOdo, setEditEndOdo] = useState('');
  const [editEndNote, setEditEndNote] = useState('');
  const [editPauseMinutes, setEditPauseMinutes] = useState('');
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [excelMenuAnchor, setExcelMenuAnchor] = useState<null | HTMLElement>(null);
  const [csvMenuAnchor, setCsvMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedDrivers, setSelectedDrivers] = useState<Driver[]>([]);
  const { data: allDrivers } = useDrivers();
  const [showFms, setShowFms] = useState(true);
  const { data: fmsData, isLoading: isFmsLoading } = useFmsComparison(from, to, true);
  const hasFmsData = fmsData && Object.keys(fmsData).length > 0;
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>(DEFAULT_VISIBLE_COLUMNS);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showCargoDetails, setShowCargoDetails] = useState(false);

  // Create manual entry state
  const [createOpen, setCreateOpen] = useState(false);
  const [createDriver, setCreateDriver] = useState<Driver | null>(null);
  const [createTrailer, setCreateTrailer] = useState<Trailer | null>(null);
  const [createDate, setCreateDate] = useState('');
  const [createStartTime, setCreateStartTime] = useState('');
  const [createEndTime, setCreateEndTime] = useState('');
  const [createPause, setCreatePause] = useState('0');

  // Delete confirmation state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSync = async () => {
    try {
      const result = await syncWorkHours.mutateAsync();
      setSnackbar({
        open: true,
        message: `Synkroniseret ${result.synced} poster fra DORA${result.skipped > 0 ? ` (${result.skipped} sprunget over pga. manuelle ændringer)` : ''}`,
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke synkronisere med DORA', severity: 'error' });
    }
  };

  const handleFullSync = async () => {
    try {
      const result = await syncAllWorkHours.mutateAsync();
      setSnackbar({
        open: true,
        message: `Synkroniseret ${result.synced} poster fra ${result.dateRange.from} til ${result.dateRange.to}${result.skipped > 0 ? ` (${result.skipped} sprunget over)` : ''}`,
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke synkronisere med DORA', severity: 'error' });
    }
  };

  const handleDownloadDanlon = async () => {
    setIsDownloadingCsv(true);
    try {
      await api.downloadDanlonCsv(from, to);
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke downloade Danløn CSV', severity: 'error' });
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const handleDownloadDatalon = async () => {
    setIsDownloadingCsv(true);
    try {
      await api.downloadDatalonBrancheCsv(from, to);
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke downloade Dataløn Branche CSV', severity: 'error' });
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const handleDownloadExcel = async (type: 'detailed' | 'summary') => {
    setExcelMenuAnchor(null);
    setIsDownloadingExcel(true);
    try {
      const driverIds = selectedDrivers.length > 0 ? selectedDrivers.map(d => d.id) : undefined;
      if (type === 'summary') {
        await api.downloadWorkHoursSummaryExcel(from, to, driverIds);
      } else {
        await api.downloadWorkHoursExcel(from, to, driverIds);
      }
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke downloade Excel', severity: 'error' });
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const handleEdit = (entry: WorkHourEntry) => {
    setEditingEntry(entry);
    setEditStartTime(toDateTimeLocal(entry.started?.dateTime));
    setEditStartOdo(entry.started?.odometer || '');
    setEditStartNote(entry.started?.note || '');
    setEditEndTime(toDateTimeLocal(entry.stopped?.dateTime));
    setEditEndOdo(entry.stopped?.odometer || '');
    setEditEndNote(entry.stopped?.note || '');
    setEditPauseMinutes(entry.pauseMinutes?.toString() || '0');
    setEditOpen(true);
  };

  const handleOpenCreate = () => {
    setCreateDriver(null);
    setCreateTrailer(null);
    setCreateDate(new Date().toISOString().substring(0, 10));
    setCreateStartTime('');
    setCreateEndTime('');
    setCreatePause('0');
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createDriver || !createDate) return;
    try {
      await createWorkHour.mutateAsync({
        driverId: createDriver.id,
        driverName: `${createDriver.firstName} ${createDriver.lastName}`,
        trailerId: createTrailer?.id,
        trailerNumber: createTrailer?.number,
        trailerAlias: createTrailer?.alias,
        date: createDate,
        start: createStartTime ? { dateTime: new Date(createStartTime).toISOString() } : undefined,
        end: createEndTime ? { dateTime: new Date(createEndTime).toISOString() } : undefined,
        pauseMinutes: parseInt(createPause, 10) || 0,
      });
      setCreateOpen(false);
      setSnackbar({ open: true, message: 'Manuel registrering oprettet', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke oprette registrering', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteWorkHour.mutateAsync(id);
      setDeleteConfirmId(null);
      setSnackbar({ open: true, message: 'Registrering slettet', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke slette registrering', severity: 'error' });
    }
  };

  const handleSave = async () => {
    if (!editingEntry) return;

    try {
      const data: {
        driverId: string;
        pauseMinutes?: number;
        start?: { dateTime: string; odometer: string; note?: string | null };
        end?: { dateTime: string; odometer: string; note?: string | null };
      } = { driverId: editingEntry.driverId || '' };

      // Parse pause minutes
      const pauseMins = parseInt(editPauseMinutes, 10);
      if (!isNaN(pauseMins) && pauseMins >= 0) {
        data.pauseMinutes = pauseMins;
      }

      if (editStartTime) {
        data.start = {
          dateTime: new Date(editStartTime).toISOString(),
          odometer: editStartOdo,
          note: editStartNote || null,
        };
      }

      if (editEndTime) {
        data.end = {
          dateTime: new Date(editEndTime).toISOString(),
          odometer: editEndOdo,
          note: editEndNote || null,
        };
      }

      await updateTrip.mutateAsync({ routeId: editingEntry.routeId, data });
      setEditOpen(false);
      setSnackbar({ open: true, message: 'Tider opdateret', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke opdatere tider', severity: 'error' });
    }
  };

  // Create a unique key for each row (routeId + driverId) and merge FMS data
  // Filter out entries without drivers
  const selectedDriverIds = useMemo(() => new Set(selectedDrivers.map(d => d.id)), [selectedDrivers]);
  // Saml driver-navne for valgte drivere — vi filtrerer paa navn (ikke id)
  // saa baade DORA_TMS og WORKDAY-rakker for samme person inkluderes.
  const selectedDriverNames = useMemo(
    () => new Set(selectedDrivers.map(d => `${d.firstName} ${d.lastName}`.trim()).filter(Boolean)),
    [selectedDrivers],
  );

  const dataWithKey = useMemo(() => {
    return (workHours || [])
      .filter(entry => entry.driverId && entry.driverId.trim() !== '')
      .filter(entry => {
        if (selectedDriverIds.size === 0) return true;
        return selectedDriverIds.has(entry.driverId) || selectedDriverNames.has(entry.driverName);
      })
      .map((entry) => ({
        ...entry,
        _key: `${entry.routeId}-${entry.driverId}`,
        _fmsData: fmsData?.[`${entry.driverId}:${entry.date}`] as FmsComparisonEntry | undefined,
      }));
  }, [workHours, fmsData, selectedDriverIds, selectedDriverNames]);

  type WorkHourRow = typeof dataWithKey[number];

  const columns = useMemo(() => {
    const cols: Column<WorkHourRow>[] = [
      {
        id: 'date',
        label: 'Dato',
        minWidth: 100,
        format: (value: unknown) => {
          if (!value) return '-';
          try {
            const d = new Date(value as string);
            return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' });
          } catch {
            return String(value);
          }
        },
      },
      {
        id: 'driverName',
        label: 'Chauffør',
        minWidth: 140,
      },
      {
        id: 'source',
        label: 'Kilde',
        minWidth: 90,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) => {
          const src = (row as any).source as 'WORKDAY' | 'DORA_TMS' | undefined;
          const edited = (row as any).manuallyEdited;
          const label = src === 'WORKDAY' ? 'Rental-app' : src === 'DORA_TMS' ? 'DORA TMS' : '—';
          const color = src === 'WORKDAY' ? '#1976d2' : '#666';
          return (
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
              <span style={{ color, fontSize: 11, padding: '2px 6px', border: `1px solid ${color}`, borderRadius: 4 }}>{label}</span>
              {edited && <span style={{ color: '#ff9800', fontSize: 11 }} title="Manuelt redigeret">✎</span>}
            </span>
          );
        },
      },
      {
        id: 'trailerNumber',
        label: 'Køretøj',
        minWidth: 100,
        format: (_: unknown, row: WorkHourRow) =>
          row.trailerAlias || row.trailerNumber,
      },
      {
        id: 'startTime',
        label: 'Starttid',
        minWidth: 70,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          formatTime(row.started?.dateTime),
      },
    ];

    if (visibleColumns.startOdo) {
      cols.push({
        id: 'startOdo',
        label: 'Start km',
        minWidth: 90,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          formatOdometer(row.started?.odometer),
      });
    }

    if (visibleColumns.startNote) {
      cols.push({
        id: 'startNote',
        label: 'Start note',
        minWidth: 120,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          row.started?.note || '-',
      });
    }

    cols.push({
      id: 'endTime',
      label: 'Sluttid',
      minWidth: 70,
      sortable: false,
      format: (_: unknown, row: WorkHourRow) =>
        formatTime(row.stopped?.dateTime),
    });

    if (visibleColumns.endOdo) {
      cols.push({
        id: 'endOdo',
        label: 'Slut km',
        minWidth: 90,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          formatOdometer(row.stopped?.odometer),
      });
    }

    if (visibleColumns.endNote) {
      cols.push({
        id: 'endNote',
        label: 'Slut note',
        minWidth: 120,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          row.stopped?.note || '-',
      });
    }

    if (visibleColumns.pause) {
      cols.push({
        id: 'pauseMinutes',
        label: 'Pause',
        minWidth: 70,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          formatMinutes(row.pauseMinutes),
      });
    }

    if (visibleColumns.totalTime) {
      cols.push({
        id: 'totalMinutes',
        label: 'Total tid',
        minWidth: 80,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          formatMinutes(row.totalMinutes),
      });
    }

    if (visibleColumns.workTime) {
      cols.push({
        id: 'workMinutes',
        label: 'Arbejdstid',
        minWidth: 90,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          formatMinutes(row.workMinutes),
      });
    }

    if (showFms && hasFmsData) {
      cols.push({
        id: 'fmsStart',
        label: 'FMS Start',
        minWidth: 75,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          formatTime(row._fmsData?.firstSeen),
      });
      cols.push({
        id: 'fmsEnd',
        label: 'FMS Slut',
        minWidth: 75,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) =>
          formatTime(row._fmsData?.lastSeen),
      });
      cols.push({
        id: 'fmsDeltaStart',
        label: 'Start Δ',
        minWidth: 70,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) => (
          <DeviationChip doraTime={row.started?.dateTime} fmsTime={row._fmsData?.firstSeen ?? null} />
        ),
      });
      cols.push({
        id: 'fmsDeltaEnd',
        label: 'Slut Δ',
        minWidth: 70,
        sortable: false,
        format: (_: unknown, row: WorkHourRow) => (
          <DeviationChip doraTime={row.stopped?.dateTime} fmsTime={row._fmsData?.lastSeen ?? null} />
        ),
      });
    }

    cols.push({
      id: 'actions',
      label: '',
      align: 'right' as const,
      minWidth: 80,
      sortable: false,
      format: (_: unknown, row: WorkHourRow) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title="Rediger">
            <IconButton size="small" onClick={() => handleEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.routeId.startsWith('manual-') && (
            <Tooltip title="Slet">
              <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(row.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    });

    return cols;
  }, [visibleColumns, showFms, hasFmsData, fmsData]);

  if (error) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>Arbejdstimer</Typography>
        <Alert severity="error">Kunne ikke hente arbejdstimer.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Arbejdstimer</Typography>
          <Tooltip title="Kolonneindstillinger">
            <IconButton
              size="small"
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              color={showColumnSettings ? 'primary' : 'default'}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Opret timeregistrering">
            <IconButton size="small" onClick={handleOpenCreate} color="success">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Synkroniser fra DORA">
            <IconButton
              size="small"
              onClick={handleSync}
              disabled={syncWorkHours.isPending || syncAllWorkHours.isPending}
              color="primary"
            >
              {syncWorkHours.isPending ? <CircularProgress size={18} /> : <SyncIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Hent al historik (fra sidste år)">
            <IconButton
              size="small"
              onClick={handleFullSync}
              disabled={syncWorkHours.isPending || syncAllWorkHours.isPending}
              color="secondary"
            >
              {syncAllWorkHours.isPending ? <CircularProgress size={18} /> : <HistoryIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Download løn-CSV (Danløn eller Dataløn Branche)">
            <IconButton
              size="small"
              onClick={(e) => setCsvMenuAnchor(e.currentTarget)}
              disabled={isDownloadingCsv}
              color="default"
            >
              {isDownloadingCsv ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={csvMenuAnchor}
            open={Boolean(csvMenuAnchor)}
            onClose={() => setCsvMenuAnchor(null)}
          >
            <MenuItem onClick={() => { setCsvMenuAnchor(null); handleDownloadDanlon(); }}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Danløn" secondary="CVR;medarbejder;løntype;timer" />
            </MenuItem>
            <MenuItem onClick={() => { setCsvMenuAnchor(null); handleDownloadDatalon(); }}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Dataløn Branche" secondary="Pr. lønart + sats fra Settings" />
            </MenuItem>
          </Menu>
          <Tooltip title="Download Excel-rapport">
            <IconButton
              size="small"
              onClick={(e) => setExcelMenuAnchor(e.currentTarget)}
              disabled={isDownloadingExcel}
              color="default"
            >
              {isDownloadingExcel ? <CircularProgress size={18} /> : <TableChartIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={excelMenuAnchor}
            open={Boolean(excelMenuAnchor)}
            onClose={() => setExcelMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleDownloadExcel('detailed')}>
              <ListItemIcon><TableChartIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Detaljeret (ark per chauffør)</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleDownloadExcel('summary')}>
              <ListItemIcon><SummarizeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Sammendrag (alle på ét ark)</ListItemText>
            </MenuItem>
          </Menu>
          {(hasFmsData || isFmsLoading) && (
            <Tooltip title={showFms ? 'Skjul FMS GPS-tider' : 'Vis FMS GPS-tider'}>
              <IconButton
                size="small"
                onClick={() => setShowFms(prev => !prev)}
                color={showFms ? 'primary' : 'default'}
              >
                {isFmsLoading ? <CircularProgress size={18} /> : <GpsFixedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {lastSyncedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Sidst synkroniseret: {new Date(lastSyncedAt).toLocaleString('da-DK')}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Autocomplete
            multiple
            size="small"
            options={allDrivers || []}
            getOptionLabel={(d) => `${d.firstName} ${d.lastName}`}
            value={selectedDrivers}
            onChange={(_, value) => setSelectedDrivers(value)}
            limitTags={3}
            renderInput={(params) => (
              <TextField {...params} placeholder={selectedDrivers.length === 0 ? 'Filtrer chauffører...' : ''} />
            )}
            sx={{ minWidth: 280 }}
          />
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </Box>
      </Box>

      {/* Column visibility settings */}
      {showColumnSettings && (
        <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Vis kolonner</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={visibleColumns.startOdo} onChange={() => toggleColumn('startOdo')} size="small" />}
              label="Start km"
            />
            <FormControlLabel
              control={<Checkbox checked={visibleColumns.endOdo} onChange={() => toggleColumn('endOdo')} size="small" />}
              label="Slut km"
            />
            <FormControlLabel
              control={<Checkbox checked={visibleColumns.startNote} onChange={() => toggleColumn('startNote')} size="small" />}
              label="Start note"
            />
            <FormControlLabel
              control={<Checkbox checked={visibleColumns.endNote} onChange={() => toggleColumn('endNote')} size="small" />}
              label="Slut note"
            />
            <FormControlLabel
              control={<Checkbox checked={visibleColumns.pause} onChange={() => toggleColumn('pause')} size="small" />}
              label="Pause"
            />
            <FormControlLabel
              control={<Checkbox checked={visibleColumns.totalTime} onChange={() => toggleColumn('totalTime')} size="small" />}
              label="Total tid"
            />
            <FormControlLabel
              control={<Checkbox checked={visibleColumns.workTime} onChange={() => toggleColumn('workTime')} size="small" />}
              label="Arbejdstid"
            />
            <FormControlLabel
              control={<Checkbox checked={showCargoDetails} onChange={() => setShowCargoDetails(prev => !prev)} size="small" />}
              label="Vis ordretimer"
            />
          </Box>
        </Paper>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : dataWithKey.length > 0 ? (
        <DataTable
          columns={columns}
          data={dataWithKey}
          rowKey="_key"
          searchPlaceholder="Søg chauffør, køretøj..."
          renderExpandedRow={showCargoDetails ? (row) => {
            const details = row.cargoDetails;
            if (!details || details.length === 0) return null;
            return (
              <Table size="small" sx={{ bgcolor: 'grey.50' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Kunde</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Start</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Slut</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Pause</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Arbejdstid</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Reference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((cd) => (
                    <TableRow key={cd.cargoId}>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{cd.customerName || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{formatTime(cd.startedAt)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{formatTime(cd.endedAt)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{formatMinutes(cd.pauseMinutes)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{formatMinutes(cd.workMinutes)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{cd.cargoRef}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          } : undefined}
        />
      ) : (
        <Alert severity="info">Ingen arbejdstimer fundet i den valgte periode.</Alert>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rediger tider</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {editingEntry?.driverName} &middot; {editingEntry?.trailerAlias || editingEntry?.trailerNumber}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Start</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Dato"
                type="date"
                value={editStartTime.slice(0, 10)}
                onChange={(e) => setEditStartTime(`${e.target.value}T${editStartTime.slice(11) || '00:00'}`)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Tid"
                type="time"
                value={editStartTime.slice(11, 16)}
                onChange={(e) => setEditStartTime(`${editStartTime.slice(0, 10) || new Date().toISOString().slice(0, 10)}T${e.target.value}`)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Kilometertæller"
              value={editStartOdo}
              onChange={(e) => setEditStartOdo(e.target.value)}
              size="small"
            />
            <TextField
              label="Note"
              value={editStartNote}
              onChange={(e) => setEditStartNote(e.target.value)}
              size="small"
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Slut</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Dato"
                type="date"
                value={editEndTime.slice(0, 10)}
                onChange={(e) => setEditEndTime(`${e.target.value}T${editEndTime.slice(11) || '00:00'}`)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: 1 }}
                helperText="OBS: husk at vælge dagen efter ved nattevagter"
              />
              <TextField
                label="Tid"
                type="time"
                value={editEndTime.slice(11, 16)}
                onChange={(e) => setEditEndTime(`${editEndTime.slice(0, 10) || new Date().toISOString().slice(0, 10)}T${e.target.value}`)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Kilometertæller"
              value={editEndOdo}
              onChange={(e) => setEditEndOdo(e.target.value)}
              size="small"
            />
            <TextField
              label="Note"
              value={editEndNote}
              onChange={(e) => setEditEndNote(e.target.value)}
              size="small"
            />

            {/* Live varighed-visning */}
            {(() => {
              if (!editStartTime || !editEndTime) return null;
              const s = new Date(editStartTime).getTime();
              const e = new Date(editEndTime).getTime();
              if (isNaN(s) || isNaN(e)) return null;
              const diffMin = Math.round((e - s) / 60000);
              const pauseM = parseInt(editPauseMinutes, 10) || 0;
              const workMin = diffMin - pauseM;
              const h = Math.floor(Math.abs(workMin) / 60);
              const m = Math.abs(workMin) % 60;
              const isError = diffMin < 0;
              const isShort = diffMin > 0 && diffMin < 30;
              const crossesMidnight = editStartTime.slice(0, 10) !== editEndTime.slice(0, 10);
              return (
                <Alert
                  severity={isError ? 'error' : isShort ? 'warning' : 'info'}
                  sx={{ mt: 1 }}
                >
                  {isError ? (
                    <strong>Slut ligger før start — kontrollér datoerne</strong>
                  ) : (
                    <>
                      <strong>Arbejdstid: {h}t {m}min</strong>
                      {pauseM > 0 && ` (efter ${pauseM}min pause)`}
                      {crossesMidnight && ' · går over midnat'}
                      {isShort && <div>Varighed under 30 min — er det rigtigt?</div>}
                    </>
                  )}
                </Alert>
              );
            })()}

            <Typography variant="subtitle2" sx={{ mt: 1 }}>Pause</Typography>
            <TextField
              label="Pause (minutter)"
              type="number"
              value={editPauseMinutes}
              onChange={(e) => setEditPauseMinutes(e.target.value)}
              size="small"
              inputProps={{ min: 0 }}
              helperText="Samlet pausetid i minutter"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updateTrip.isPending || !editingEntry}
          >
            {updateTrip.isPending ? <CircularProgress size={24} /> : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Manual Entry Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Opret timeregistrering</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Autocomplete
              options={allDrivers || []}
              getOptionLabel={(d) => `${d.firstName} ${d.lastName}`}
              value={createDriver}
              onChange={(_, v) => setCreateDriver(v)}
              renderInput={(params) => <TextField {...params} label="Chauffør" size="small" />}
            />
            <Autocomplete
              options={allTrailers || []}
              getOptionLabel={(t) => t.alias || t.number}
              value={createTrailer}
              onChange={(_, v) => setCreateTrailer(v)}
              renderInput={(params) => <TextField {...params} label="Køretøj (valgfrit)" size="small" />}
            />
            <TextField
              label="Dato"
              type="date"
              value={createDate}
              onChange={(e) => setCreateDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Starttid"
              type="datetime-local"
              value={createStartTime}
              onChange={(e) => setCreateStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Sluttid"
              type="datetime-local"
              value={createEndTime}
              onChange={(e) => setCreateEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="Pause (minutter)"
              type="number"
              value={createPause}
              onChange={(e) => setCreatePause(e.target.value)}
              size="small"
              inputProps={{ min: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annuller</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createWorkHour.isPending || !createDriver || !createDate}
          >
            {createWorkHour.isPending ? <CircularProgress size={24} /> : 'Opret'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Slet registrering?</DialogTitle>
        <DialogContent>
          <Typography>Er du sikker på at du vil slette denne manuelle registrering?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Annuller</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
            disabled={deleteWorkHour.isPending}
          >
            {deleteWorkHour.isPending ? <CircularProgress size={24} /> : 'Slet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
